import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Instagram, Facebook, MapPin, Star, Youtube } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/sr8wypsw_1.png';

const SOCIAL_LINKS = {
  instagram: 'https://www.instagram.com/abbamassage',
  facebook: 'https://www.facebook.com/abbamassage',
  youtube: 'https://www.youtube.com/@abbamassage',
  googleMaps: 'https://maps.app.goo.gl/G2JL5nKRZdTESBVXA',
  tripadvisor: 'https://www.tripadvisor.com.mx/Attraction_Review-g150807-d34151326-Reviews-Abba_Massage_Studio_and_Wellness_Center-Cancun_Yucatan_Peninsula.html'
};

export const Footer = () => {
  const { t, language } = useLanguage();

  const quickLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/hosts', label: t('nav.hosts') },
    { href: '/community', label: t('nav.community') },
    { href: '/articles', label: language === 'es' ? 'Artículos' : 'Articles' },
    { href: '/contact', label: t('nav.contact') },
  ];

  const serviceLinks = [
    { href: '/in-home-massage', label: t('nav.inHome') },
    { href: '/studio-services', label: t('nav.studio') },
    { href: '/energy-work', label: t('nav.energy') },
    { href: '/janzu', label: t('nav.janzu') },
    { href: '/events', label: t('nav.events') },
  ];

  return (
    <footer className="bg-[#1C1C1C] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={LOGO_URL} 
                alt="ABBA Massage" 
                className="h-16 w-auto object-contain"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </Link>
            <p className="text-[#A0A0A0] text-sm leading-relaxed font-['Playfair_Display'] italic">
              {t('footer.tagline')}
            </p>
            
            {/* Social Icons */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-white/10 hover:bg-[#A91B60] transition-colors"
                data-testid="footer-instagram"
                title="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-white/10 hover:bg-[#A91B60] transition-colors"
                data-testid="footer-facebook"
                title="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-white/10 hover:bg-[#A91B60] transition-colors"
                data-testid="footer-youtube"
                title="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href={SOCIAL_LINKS.tripadvisor}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-white/10 hover:bg-[#A91B60] transition-colors"
                data-testid="footer-tripadvisor"
                title="TripAdvisor"
              >
                <Star className="w-5 h-5" />
              </a>
              <a
                href={SOCIAL_LINKS.googleMaps}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-white/10 hover:bg-[#A91B60] transition-colors"
                data-testid="footer-maps"
                title="Google Maps"
              >
                <MapPin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-6 font-['Manrope'] font-semibold">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-[#A0A0A0] hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-6 font-['Manrope'] font-semibold">
              {t('footer.services')}
            </h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-[#A0A0A0] hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-6 font-['Manrope'] font-semibold">
              {t('footer.connect')}
            </h4>
            <ul className="space-y-4 text-sm text-[#A0A0A0]">
              <li>
                <a href="mailto:hello@abbamassage.com" className="hover:text-white transition-colors">
                  hello@abbamassage.com
                </a>
              </li>
              <li>
                <a href="tel:+529983032313" className="hover:text-white transition-colors">
                  +52 998 303 2313
                </a>
              </li>
              <li className="leading-relaxed">
                <a 
                  href={SOCIAL_LINKS.googleMaps}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  Calle Playa Brava 26, Mz. 1, Lote 53<br />
                  Cancún, Quintana Roo<br />
                  CP 77508, México
                </a>
              </li>
            </ul>
            
            <div className="mt-6">
              <h5 className="text-xs uppercase tracking-wider text-[#5A5A5A] mb-2">
                {t('footer.payment')}
              </h5>
              <p className="text-xs text-[#A0A0A0]">
                {t('footer.paymentMethods')}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-sm text-[#5A5A5A]">
            © {new Date().getFullYear()} ABBA Massage. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
